import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Edit2 } from 'react-feather';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { toast } from 'react-toastify';
import { getModelRoutingAPI, testRouteLatencyAPI, updateModelRoutingAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { platformUrl } from '../../../utils/mhNav';
import styles from './ModelRouting.module.scss';

export default function ModelRouting() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [latMap, setLatMap] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getModelRoutingAPI();
      setRows((res.data || []).map((r) => ({ ...r })));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (r) => {
    setEditId(r.id);
    setDraft({ ...r });
  };

  const saveRow = () => {
    if (!draft) return;
    setRows((prev) => prev.map((x) => (x.id === draft.id ? { ...draft } : x)));
    setEditId(null);
    setDraft(null);
    toast.success('Row updated locally — use Save all to persist');
  };

  const saveAll = async () => {
    try {
      await updateModelRoutingAPI(rows);
      toast.success('Routes saved');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const testLat = async (rid) => {
    try {
      const res = await testRouteLatencyAPI(rid);
      setLatMap((m) => ({ ...m, [rid]: res.data.latencyMs }));
      toast.success(`${res.data.latencyMs} ms`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const chartLat = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      xaxis: { categories: rows.map((r) => r.module) },
      plotOptions: { bar: { horizontal: false, columnWidth: '55%' } },
      dataLabels: { enabled: false },
    }),
    [rows]
  );

  const chartCost = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      xaxis: { categories: rows.map((r) => r.module) },
      plotOptions: { bar: { horizontal: true, barHeight: '70%' } },
      dataLabels: { enabled: false },
    }),
    [rows]
  );

  if (loading && !rows.length) {
    return (
      <div>
        <PageHeader title="Model routing" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'AI policies', path: platformUrl('ai-policies') }, { label: 'Routing' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Model routing" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'AI policies', path: platformUrl('ai-policies') }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Model routing"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'AI policies', path: platformUrl('ai-policies') },
          { label: 'Routing' },
        ]}
        primaryAction={{ label: 'Add route', onClick: () => toast.info('Add route (simulated)') }}
      />

      <div className={`${styles.stickyFilters} mb-2`}>
        <Button as={Link} variant="outline-secondary" size="sm" to={platformUrl('ai-policies')}>
          Back to policies
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body className="p-0">
          <Table responsive bordered hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Module</th>
                <th>Primary</th>
                <th>Fallback</th>
                <th>Timeout (ms)</th>
                <th>Token limit</th>
                <th>Status</th>
                <th>Latency test</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const editing = editId === r.id;
                const row = editing && draft ? draft : r;
                return (
                  <tr key={r.id}>
                    <td>{editing ? <Form.Control size="sm" value={row.module} onChange={(e) => setDraft({ ...row, module: e.target.value })} /> : row.module}</td>
                    <td>
                      {editing ? (
                        <Form.Control size="sm" value={row.primaryModel} onChange={(e) => setDraft({ ...row, primaryModel: e.target.value })} />
                      ) : (
                        row.primaryModel
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control size="sm" value={row.fallbackModel} onChange={(e) => setDraft({ ...row, fallbackModel: e.target.value })} />
                      ) : (
                        row.fallbackModel
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          value={row.timeoutMs}
                          onChange={(e) => setDraft({ ...row, timeoutMs: +e.target.value })}
                        />
                      ) : (
                        row.timeoutMs
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          value={row.tokenLimit}
                          onChange={(e) => setDraft({ ...row, tokenLimit: +e.target.value })}
                        />
                      ) : (
                        row.tokenLimit
                      )}
                    </td>
                    <td>
                      {row.status === 'pending_approval' ? (
                        <Badge bg="warning" text="dark">
                          Pending approval
                        </Badge>
                      ) : (
                        <Badge bg="success">Active</Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <Button size="sm" variant="outline-secondary" onClick={() => testLat(r.id)}>
                          Test latency
                        </Button>
                        {latMap[r.id] != null && <span className="small text-muted">{latMap[r.id]} ms</span>}
                      </div>
                    </td>
                    <td>
                      {editing ? (
                        <div className="d-flex gap-1">
                          <Button size="sm" onClick={saveRow}>
                            OK
                          </Button>
                          <Button
                            size="sm"
                            variant="light"
                            onClick={() => {
                              setEditId(null);
                              setDraft(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline-primary" onClick={() => startEdit(r)}>
                          <Edit2 size={14} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Card>
            <Card.Header>Average latency by route</Card.Header>
            <Card.Body>
              <Chart
                options={chartLat}
                series={[{ name: 'Avg ms', data: rows.map((r) => r.avgLatencyMs) }]}
                type="bar"
                height={280}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <Card.Header>Estimated cost by route</Card.Header>
            <Card.Body>
              <Chart
                options={chartCost}
                series={[{ name: 'USD / 1k tok', data: rows.map((r) => +r.estCostUsdPer1k.toFixed(4)) }]}
                type="bar"
                height={280}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className={styles.stickySave}>
        <Button onClick={saveAll}>Save all changes</Button>
      </div>
    </div>
  );
}
